import { useSyncExternalStore } from "react";
import {
  loadSessionRecordingConsent,
  saveSessionRecordingConsent,
  type SessionRecordingConsent,
} from "_shared/session-recording/session-recording-consent";
import { startSessionRecording, stopSessionRecording } from "_shared/session-recording/session-recorder";
import { SESSION_ESTABLISHED_EVENT } from "users/__session/session";

type StorageLike = Pick<Storage, "getItem" | "setItem">;

type Recorder = {
  start: () => void;
  stop: () => void;
};

/** Raised by `Session.logout()`. */
const SESSION_ENDED_EVENT = "loggedOut";

export type SessionRecordingChoiceSnapshot = {
  readonly consent: SessionRecordingConsent | undefined;
  /** Whether the question is pending: never answered, or reopened from the footer. */
  readonly isOpen: boolean;
  readonly isSignedIn: boolean;
};

const browserRecorder: Recorder = {
  start: () => startSessionRecording(),
  stop: () => stopSessionRecording(),
};

/**
 * Whether FullStory records, decided in one place: only while someone is signed in, and only when
 * this device has said yes.
 *
 * Nothing is recorded before sign-in, so there is nothing to ask about before sign-in either. The
 * question waits for the signed-in shell, and the login form never has a banner over it.
 *
 * Every change — an answer, a withdrawal, a sign-in, a sign-out — goes through one update that
 * starts capture when it becomes allowed and stops it when it stops being allowed, so no path can
 * leave a recorder running that the rules say should be off.
 *
 * Sign-in and sign-out arrive as the document events `Session` already raises. `logout()` changes
 * the session in place, so React alone would not see it; the realtime middleware listens to the
 * same events for the same reason. The state at boot, before any event, comes from the banner,
 * which reads the restored session.
 *
 * Shared by the banner and by the footer entry that reopens it. The footer is mounted separately
 * by three layouts, so they meet here rather than in a React context one of them might sit outside.
 */
export class SessionRecordingChoice {
  public constructor(
    storage?: StorageLike,
    recorder: Recorder = browserRecorder,
    sessionEvents: EventTarget | undefined = globalThis.document,
  ) {
    this.storage = storage;
    this.recorder = recorder;
    const consent = loadSessionRecordingConsent(this.storage);
    this.snapshot = { consent, isOpen: consent === undefined, isSignedIn: false };
    sessionEvents?.addEventListener(SESSION_ESTABLISHED_EVENT, () => this.setSignedIn(true));
    sessionEvents?.addEventListener(SESSION_ENDED_EVENT, () => this.setSignedIn(false));
  }

  public accept = (): void => {
    saveSessionRecordingConsent("granted", this.storage);
    this.update({ ...this.snapshot, consent: "granted", isOpen: false });
  };

  /** Withdraws a yes as readily as it was given: capture stops now, not on the next visit. */
  public decline = (): void => {
    saveSessionRecordingConsent("declined", this.storage);
    this.update({ ...this.snapshot, consent: "declined", isOpen: false });
  };

  /** Puts the question away and leaves the answer as it was. Only offered once there is an answer to keep. */
  public close = (): void => {
    this.update({ ...this.snapshot, isOpen: false });
  };

  public getSnapshot = (): SessionRecordingChoiceSnapshot => this.snapshot;

  public reopen = (): void => {
    this.update({ ...this.snapshot, isOpen: true });
  };

  public setSignedIn = (isSignedIn: boolean): void => {
    if (this.snapshot.isSignedIn === isSignedIn) return;
    this.update({ ...this.snapshot, isSignedIn });
  };

  public subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private readonly listeners = new Set<() => void>();
  private readonly recorder: Recorder;
  private snapshot: SessionRecordingChoiceSnapshot;
  private readonly storage: StorageLike | undefined;

  private update(snapshot: SessionRecordingChoiceSnapshot): void {
    const wasRecording = isRecordingAllowed(this.snapshot);
    this.snapshot = snapshot;
    const isRecording = isRecordingAllowed(snapshot);

    if (isRecording && !wasRecording) this.recorder.start();
    if (wasRecording && !isRecording) this.recorder.stop();

    this.listeners.forEach((listener) => listener());
  }
}

function isRecordingAllowed(snapshot: SessionRecordingChoiceSnapshot): boolean {
  return snapshot.isSignedIn && snapshot.consent === "granted";
}

export const sessionRecordingChoice = new SessionRecordingChoice();

export function useSessionRecordingChoice(
  choice: SessionRecordingChoice = sessionRecordingChoice,
): SessionRecordingChoiceSnapshot {
  return useSyncExternalStore(choice.subscribe, choice.getSnapshot);
}
